// ============================================================================
// DDARP API Client — Cookie-based auth (P4 rewrite, 2026-05-23)
// ============================================================================
// Changes from the previous Bearer-token version:
// - withCredentials: true → cookies (__Host-ddarp_session + ddarp_csrf) flow
// - localStorage Bearer token logic REMOVED
// - CSRF double-submit: read ddarp_csrf cookie, echo as X-CSRF-Token header
//   on every mutating request (POST/PUT/PATCH/DELETE)
// - 401 handling: soft event instead of hard window.location redirect; the
//   AuthContext listens and routes to /login via react-router
// - authApi.login() calls /api/auth-new/login (new endpoint during P3-P6
//   "both" window; P7 cutover will rename it to /api/auth/login)
// ============================================================================

import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';

// Backend API URL - uses relative path in production (nginx proxies /api to backend)
// In development, set VITE_API_URL to point to the backend
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Endpoint prefix toggle: during P3-P6 "both" mode the new auth router lives
// at /api/auth-new/*; at P7 cutover this becomes /api/auth/*. VITE_AUTH_PATH
// lets us flip via env without code change.
const AUTH_PATH = import.meta.env.VITE_AUTH_PATH || '/api/auth-new';

// Event name dispatched on 401 so AuthContext can react without a hard redirect.
export const AUTH_UNAUTHORIZED_EVENT = 'ddarp:unauthorized';

// Read a cookie by name from document.cookie (browser-only).
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
}

// Create axios instance with defaults
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 120 seconds for SSH operations (nodes endpoint can be slow)
  withCredentials: true,  // P4: send cookies with every request
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — CSRF double-submit on mutating methods
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const method = (config.method || 'get').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
      const csrf = readCookie('ddarp_csrf');
      if (csrf) {
        config.headers.set('X-CSRF-Token', csrf);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — emit a soft "unauthorized" event on 401 so the
// AuthContext can show the login screen via react-router instead of a
// hard window.location redirect (which would lose any unsaved state).
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// API Functions - All data from REAL production nodes
// ============================================================================

// --- Authentication (new cookie-based, P4) ---
export interface MeResponse {
  id: number;
  email: string;
  role: 'admin' | 'partner' | 'user';
  username?: string | null;
  company?: string | null;
  last_login_at?: string | null;
  last_login_ip?: string | null;
}

export const authApi = {
  /** New cookie-based login. Cookies (__Host-ddarp_session + ddarp_csrf)
   *  are set by the backend; no client-side storage. */
  login: async (email: string, password: string, rememberMe: boolean = false) => {
    const response = await apiClient.post(`${AUTH_PATH}/login`, {
      email,
      password,
      remember_me: rememberMe,
    });
    return response.data as MeResponse;
  },

  logout: async () => {
    const response = await apiClient.post(`${AUTH_PATH}/logout`);
    return response.data;
  },

  /** Identify the current user from the session cookie. 401 if no session. */
  me: async (): Promise<MeResponse> => {
    const response = await apiClient.get(`${AUTH_PATH}/me`);
    return response.data;
  },

  /** Public: request a password reset email. Always returns 200. */
  forgotPassword: async (email: string) => {
    const response = await apiClient.post(`${AUTH_PATH}/forgot-password`, { email });
    return response.data;
  },

  /** Public: validate an invitation token without consuming it. */
  invitationStatus: async (token: string) => {
    const response = await apiClient.get(`${AUTH_PATH}/invitation/${encodeURIComponent(token)}`);
    return response.data as { email_masked: string; role: 'admin' | 'partner' | 'user'; valid: boolean };
  },

  /** Public: accept an invitation (sets password + logs in). */
  invitationAccept: async (token: string, password: string): Promise<MeResponse> => {
    const response = await apiClient.post(
      `${AUTH_PATH}/invitation/${encodeURIComponent(token)}/accept`,
      { password }
    );
    return response.data;
  },

  /** Public: validate a password-reset token without consuming it. */
  resetStatus: async (token: string) => {
    const response = await apiClient.get(`${AUTH_PATH}/reset/${encodeURIComponent(token)}`);
    return response.data as { email_masked: string; valid: boolean };
  },

  /** Public: confirm a password-reset (sets new password, revokes all sessions). */
  resetConfirm: async (token: string, password: string) => {
    const response = await apiClient.post(
      `${AUTH_PATH}/reset/${encodeURIComponent(token)}/confirm`,
      { password }
    );
    return response.data;
  },

  /** Authenticated: change own password. Revokes other sessions. */
  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await apiClient.post(`${AUTH_PATH}/password/change`, {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  /** Authenticated: list own active sessions. */
  listSessions: async () => {
    const response = await apiClient.get(`${AUTH_PATH}/sessions`);
    return response.data as Array<{
      id: string;
      created_at: string;
      last_used_at: string;
      ip: string | null;
      user_agent: string | null;
      remember_me: boolean;
      is_current: boolean;
    }>;
  },

  /** Authenticated: revoke one own session (not current). */
  revokeSession: async (sessionId: string) => {
    const response = await apiClient.delete(`${AUTH_PATH}/sessions/${encodeURIComponent(sessionId)}`);
    return response.data;
  },

  /** Authenticated: logout everywhere including current. */
  revokeAllSessions: async () => {
    const response = await apiClient.post(`${AUTH_PATH}/sessions/revoke-all`);
    return response.data;
  },
};

// ============================================================================
// App Settings — the runtime "Enable Tabs" cluster gate (2026-06-07)
// ============================================================================

export interface TabsEnabled {
  tabs_enabled: boolean;
}

export interface RequestEnableResult {
  sent: boolean;
  cooldown_remaining_seconds: number;
}

export const appSettingsApi = {
  /** Any authenticated user: read the current runtime settings. */
  get: async (): Promise<TabsEnabled> => {
    const response = await apiClient.get('/api/app-settings');
    return response.data as TabsEnabled;
  },

  /** Admin only: flip the Enable-Tabs gate. */
  setTabsEnabled: async (enabled: boolean): Promise<TabsEnabled> => {
    const response = await apiClient.put('/api/admin/app-settings', { tabs_enabled: enabled });
    return response.data as TabsEnabled;
  },

  /** Any authenticated user: ask the admins to enable the tabs. Throws on 429
   *  when within the 5-minute cooldown — read the remaining seconds from
   *  err.response.data.detail.cooldown_remaining_seconds. */
  requestEnable: async (): Promise<RequestEnableResult> => {
    const response = await apiClient.post('/api/app-settings/request-enable');
    return response.data as RequestEnableResult;
  },
};

// ============================================================================
// Admin: User Management (P6) — all admin-only
// ============================================================================

export type UserStatus = 'active' | 'pending' | 'disabled';
export type UserRole = 'admin' | 'partner' | 'user';

export interface AdminUserSummary {
  id: number;
  email: string;
  role: UserRole;
  status: UserStatus;
  username: string | null;
  company: string | null;
  created_at: string;
  last_login_at: string | null;
  created_by: number | null;
}

export interface AdminUserDetail extends AdminUserSummary {
  last_login_ip: string | null;
  last_login_ua: string | null;
  failed_login_count: number;
  locked_until: string | null;
  invited_by: string | null;
  invited_by_username: string | null;
  external_subject: string | null;
}

export interface AdminUserList {
  total: number;
  page: number;
  per_page: number;
  items: AdminUserSummary[];
}

export interface AdminUserSession {
  id: string;
  created_at: string;
  last_used_at: string;
  ip: string | null;
  user_agent: string | null;
  remember_me: boolean;
}

export const adminUsersApi = {
  list: async (params: {
    status?: UserStatus;
    role?: UserRole;
    search?: string;
    page?: number;
    per_page?: number;
  } = {}): Promise<AdminUserList> => {
    const response = await apiClient.get('/api/admin/users', { params });
    return response.data;
  },

  get: async (id: number): Promise<AdminUserDetail> => {
    const response = await apiClient.get(`/api/admin/users/${id}`);
    return response.data;
  },

  invite: async (
    email: string,
    role: UserRole,
    username: string,
    company: string | null = null,
  ): Promise<AdminUserDetail> => {
    const response = await apiClient.post('/api/admin/users/invite', {
      email, role, username, company,
    });
    return response.data;
  },

  /** Hard-delete a user (Cognito + DB + audit). Returns 204 on success. */
  hardDelete: async (id: number) => {
    const response = await apiClient.delete(`/api/admin/users/${id}/purge`);
    return response.data;
  },

  resendInvite: async (id: number) => {
    const response = await apiClient.post(`/api/admin/users/${id}/resend-invite`);
    return response.data;
  },

  disable: async (id: number) => {
    const response = await apiClient.post(`/api/admin/users/${id}/disable`);
    return response.data;
  },

  enable: async (id: number) => {
    const response = await apiClient.post(`/api/admin/users/${id}/enable`);
    return response.data;
  },

  changeRole: async (id: number, role: UserRole) => {
    const response = await apiClient.patch(`/api/admin/users/${id}/role`, { role });
    return response.data;
  },

  getSessions: async (id: number): Promise<AdminUserSession[]> => {
    const response = await apiClient.get(`/api/admin/users/${id}/sessions`);
    return response.data;
  },

  revokeSessions: async (id: number) => {
    const response = await apiClient.post(`/api/admin/users/${id}/sessions/revoke`);
    return response.data;
  },
};

// ============================================================================
// Admin: Audit Log (P6)
// ============================================================================

export interface AuditEntry {
  id: number;
  timestamp: string;
  operation: string;
  target_type: string;
  target_id: string | null;
  initiated_by: string | null;
  status: string;
  details: Record<string, unknown> | null;
  error_message: string | null;
  duration_ms: number | null;
}

export interface AuditPage {
  total: number;
  page: number;
  per_page: number;
  items: AuditEntry[];
}

export const auditApi = {
  list: async (params: {
    user_id?: number;
    user_email?: string;
    operation?: string;
    status?: string;
    from?: string;  // ISO datetime
    to?: string;
    page?: number;
    per_page?: number;
  } = {}): Promise<AuditPage> => {
    const response = await apiClient.get('/api/admin/audit', { params });
    return response.data;
  },
};

// --- Node Management (REAL 10 multi-cloud nodes) ---
export const nodesApi = {
  // Get all nodes with current status (FAST - uses direct HTTP to agents)
  getAll: async () => {
    const response = await apiClient.get('/api/admin/nodes/quick');
    return response.data;
  },

  // Get all nodes with SSH-based status (slower but more detailed)
  getAllDetailed: async () => {
    const response = await apiClient.get('/api/admin/nodes');
    return response.data;
  },

  // Get single node details
  getById: async (nodeId: string) => {
    const response = await apiClient.get(`/api/admin/nodes/${nodeId}`);
    return response.data;
  },

  // Get node services status via SSH
  getServices: async (nodeId: string) => {
    const response = await apiClient.get(`/api/admin/nodes/${nodeId}/services`);
    return response.data;
  },

  // Control service on a node
  controlService: async (nodeId: string, service: string, action: string) => {
    const response = await apiClient.post(
      `/api/admin/nodes/${nodeId}/services/${service}/${action}`
    );
    return response.data;
  },

  // Bulk service control
  bulkServiceControl: async (action: string, service: string, nodeIds: string[] | 'all') => {
    const response = await apiClient.post(`/api/admin/services/bulk/${action}`, {
      service,
      node_ids: nodeIds,
    });
    return response.data;
  },

  // Get node logs
  getLogs: async (nodeId: string, service: string, lines: number = 100) => {
    const response = await apiClient.get(`/api/admin/nodes/${nodeId}/logs/${service}`, {
      params: { lines },
    });
    return response.data;
  },
};

// --- WireGuard Status (REAL tunnel data) ---
export const wireguardApi = {
  // Get all WireGuard status from all nodes
  getAll: async () => {
    const response = await apiClient.get('/api/admin/wireguard');
    return response.data;
  },

  // Get WireGuard status for specific node
  getByNode: async (nodeId: string) => {
    const response = await apiClient.get(`/api/admin/wireguard/${nodeId}`);
    return response.data;
  },
};

// --- BGP Sessions (REAL BIRD data) ---
export const bgpApi = {
  // Get all BGP sessions from all nodes
  getAll: async () => {
    const response = await apiClient.get('/api/admin/bgp');
    return response.data;
  },

  // Get BGP sessions for specific node
  getByNode: async (nodeId: string) => {
    const response = await apiClient.get(`/api/admin/bgp/${nodeId}`);
    return response.data;
  },
};

// --- OWL Latency Matrix (REAL measurements) ---
export const latencyApi = {
  // Get full latency matrix
  getMatrix: async () => {
    const response = await apiClient.get('/api/admin/latency/matrix');
    return response.data;
  },

  // Get latency between two nodes
  getPair: async (source: string, target: string) => {
    const response = await apiClient.get(`/api/admin/latency/${source}/${target}`);
    return response.data;
  },

  // Get latency history
  getHistory: async (source: string, target: string, hours: number = 24) => {
    const response = await apiClient.get(`/api/admin/latency/${source}/${target}/history`, {
      params: { hours },
    });
    return response.data;
  },
};

// --- PHASE 4: ML Features ---
export const mlApi = {
  // LSTM Predictions
  getPredictions: async (nodeId?: string) => {
    const url = nodeId
      ? `/api/admin/ml/predictions/${nodeId}`
      : '/api/admin/ml/predictions';
    const response = await apiClient.get(url);
    return response.data;
  },

  // Federated Learning Status
  getFederatedStatus: async () => {
    const response = await apiClient.get('/api/admin/federated/status');
    return response.data;
  },

  // Get training status for all nodes
  getTrainingStatus: async () => {
    const response = await apiClient.get('/api/admin/federated/training');
    return response.data;
  },

  // Genetic Path Optimization
  optimizePath: async (source: string, target: string) => {
    const response = await apiClient.post('/api/admin/paths/optimize', {
      source,
      target,
    });
    return response.data;
  },

  // Get all available paths
  getPaths: async (source: string, target: string) => {
    const response = await apiClient.get(`/api/admin/paths/${source}/${target}`);
    return response.data;
  },
};

// --- Health Check ---
export const healthApi = {
  check: async () => {
    const response = await apiClient.get('/health');
    return response.data;
  },

  checkNode: async (nodeId: string) => {
    const response = await apiClient.get(`/api/admin/nodes/${nodeId}/health`);
    return response.data;
  },

  // Get full cluster health with real WireGuard and BGP data
  getClusterHealth: async () => {
    const response = await apiClient.get('/api/admin/health');
    return response.data;
  },
};

// --- Real-time Metrics ---
export const metricsApi = {
  // Get quick metrics (fast, cached)
  getRealtime: async () => {
    const response = await apiClient.get('/api/admin/metrics/quick');
    return response.data;
  },

  // Get detailed metrics (slower, SSH-based)
  getRealtimeDetailed: async () => {
    const response = await apiClient.get('/api/admin/metrics/realtime');
    return response.data;
  },
};

// --- Dashboard API (fast endpoints) ---
export const dashboardApi = {
  // Get quick dashboard data (fast, ~1-3 seconds)
  getQuick: async () => {
    const response = await apiClient.get('/api/admin/dashboard/quick');
    return response.data;
  },
};

// --- Pulse Groups and Border Nodes ---
export const pulseGroupsApi = {
  // Get all pulse groups with border nodes
  getAll: async () => {
    const response = await apiClient.get('/api/admin/pulse-groups');
    return response.data;
  },

  // Get advanced pulse groups with Raft, VRRP, heartbeat, and stitching status
  // Now fetches REAL data from production nodes
  getAdvanced: async () => {
    const response = await apiClient.get('/api/admin/pulse-groups/advanced');
    return response.data;
  },

  // Get real-time pulse group status directly from nodes
  getRealtime: async () => {
    const response = await apiClient.get('/api/admin/pulse-groups/realtime');
    return response.data;
  },

  // Get border nodes only
  getBorderNodes: async () => {
    const response = await apiClient.get('/api/admin/border-nodes');
    return response.data;
  },

  // Get Raft consensus status
  getRaftStatus: async () => {
    const response = await apiClient.get('/api/admin/border-nodes/raft-status');
    return response.data;
  },

  // Get VRRP failover status
  getVrrpStatus: async () => {
    const response = await apiClient.get('/api/admin/border-nodes/vrrp-status');
    return response.data;
  },

  // Get heartbeat health status
  getHeartbeatStatus: async () => {
    const response = await apiClient.get('/api/admin/border-nodes/heartbeat-status');
    return response.data;
  },

  // Get matrix stitching status
  getMatrixStitching: async () => {
    const response = await apiClient.get('/api/admin/border-nodes/matrix-stitching');
    return response.data;
  },

  // Trigger manual regrouping
  regroup: async () => {
    const response = await apiClient.post('/api/admin/pulse-groups/regroup');
    return response.data;
  },

  // === NEW: Centralized Coordinator Endpoints ===

  // Get coordinator status (who is the leader, cycle info, sync status)
  getCoordinator: async () => {
    const response = await apiClient.get('/api/admin/pulse-groups/coordinator');
    return response.data;
  },

  // Get current group assignments with probe reduction metrics
  getAssignments: async () => {
    const response = await apiClient.get('/api/admin/pulse-groups/assignments');
    return response.data;
  },

  // Get version consistency across all nodes
  getConsistency: async () => {
    const response = await apiClient.get('/api/admin/pulse-groups/consistency');
    return response.data;
  },

  // Get live aggregated latency matrix
  getLiveMatrix: async () => {
    const response = await apiClient.get('/api/admin/pulse-groups/live-matrix');
    return response.data;
  },
};

// --- Cluster Service Management ---
export const servicesApi = {
  // Get real service status from all nodes
  getStatus: async () => {
    const response = await apiClient.get('/api/admin/services/status');
    return response.data;
  },

  // Execute service action on cluster
  action: async (action: string, service: string, nodes?: string[]) => {
    const response = await apiClient.post('/api/admin/services/action', {
      action,
      service,
      nodes,
    });
    return response.data;
  },

  // Start all services on all nodes
  startAll: async () => {
    const response = await apiClient.post('/api/admin/services/start-all');
    return response.data;
  },

  // Stop all services on all nodes
  stopAll: async () => {
    const response = await apiClient.post('/api/admin/services/stop-all');
    return response.data;
  },

  // Restart all services on all nodes
  restartAll: async () => {
    const response = await apiClient.post('/api/admin/services/restart-all');
    return response.data;
  },

  // Execute action on specific service for specific nodes
  serviceAction: async (
    action: 'start' | 'stop' | 'restart',
    service: string,
    nodeIds?: string[]
  ) => {
    const response = await apiClient.post('/api/admin/services/action', {
      action,
      service,
      nodes: nodeIds,
    });
    return response.data;
  },
};

// --- Instance Management (EC2/GCP) ---
// Instance operations need longer timeout (up to 5 minutes for parallel cloud API calls)
const INSTANCE_TIMEOUT = 300000; // 5 minutes

export const instancesApi = {
  // Get instance status for all nodes (needs longer timeout for sequential cloud API calls)
  getStatus: async () => {
    const response = await apiClient.get('/api/admin/instances/status', {
      timeout: 180000, // 3 minutes for status check
    });
    return response.data;
  },

  // Execute instance action (start/stop) on cluster
  // Uses longer timeout as it executes cloud API calls in parallel
  action: async (action: 'start' | 'stop', nodes?: string[]) => {
    const response = await apiClient.post('/api/admin/instances/action', {
      action,
      nodes,
    }, {
      timeout: INSTANCE_TIMEOUT,
    });
    return response.data;
  },

  // Start all instances
  startAll: async () => {
    const response = await apiClient.post('/api/admin/instances/start-all', {}, {
      timeout: INSTANCE_TIMEOUT,
    });
    return response.data;
  },

  // Stop all instances
  stopAll: async () => {
    const response = await apiClient.post('/api/admin/instances/stop-all', {}, {
      timeout: INSTANCE_TIMEOUT,
    });
    return response.data;
  },

  // Start specific instances
  startInstances: async (nodeIds: string[]) => {
    const response = await apiClient.post('/api/admin/instances/action', {
      action: 'start',
      nodes: nodeIds,
    }, {
      timeout: INSTANCE_TIMEOUT,
    });
    return response.data;
  },

  // Stop specific instances
  stopInstances: async (nodeIds: string[]) => {
    const response = await apiClient.post('/api/admin/instances/action', {
      action: 'stop',
      nodes: nodeIds,
    }, {
      timeout: INSTANCE_TIMEOUT,
    });
    return response.data;
  },
};

// --- Node Lifecycle Management API ---
export interface LifecycleNode {
  node_id: string;
  name: string;
  cloud: string;
  region: string;
  instance_state: string;
  ddarp_state: string;
  public_ip: string | null;
  last_started_at: string | null;
  last_health_check: string | null;
}

export interface LifecycleService {
  service_name: string;
  status: string;
  is_critical: boolean;
  pid: number | null;
  memory_mb: number | null;
  uptime_seconds: number | null;
}

export interface LifecycleProgress {
  node_id: string;
  phase: string;
  current_step: number;
  total_steps: number;
  progress_percent: number;
  current_service: string | null;
  message: string;
  errors: string[];
}

export interface HealthCheckResult {
  check_type: string;
  check_name: string;
  status: string;
  details: Record<string, unknown> | null;
  checked_at: string;
}

export interface AuditLogEntry {
  timestamp: string;
  operation: string;
  target_type: string;
  target_id: string;
  initiated_by: string;
  status: string;
  duration_ms: number | null;
  error_message: string | null;
}

export interface ChecklistStep {
  order: number;
  title: string;
  description: string;
  check_command: string;
  fix_command: string;
  severity: string;
  auto_fixable: boolean;
}

export interface Checklist {
  name: string;
  category: string;
  steps: ChecklistStep[];
}

export interface StartupSequenceStep {
  step_order: number;
  service_name: string;
  action: string;
  timeout_seconds: number;
  retry_count: number;
  on_failure: string;
  description: string;
}

export interface HealthSummary {
  total_nodes: number;
  by_instance_state: Record<string, number>;
  by_ddarp_state: Record<string, number>;
  failures: Record<string, number>;
}

export const lifecycleApi = {
  // Get database health status
  getHealth: async (environment: string = 'prod') => {
    const response = await apiClient.get('/api/admin/lifecycle/health', {
      params: { environment },
    });
    return response.data;
  },

  // Get all nodes with lifecycle state
  getNodes: async (environment: string = 'prod'): Promise<LifecycleNode[]> => {
    const response = await apiClient.get('/api/admin/lifecycle/nodes', {
      params: { environment },
    });
    return response.data;
  },

  // Get single node details
  getNode: async (nodeId: string, environment: string = 'prod'): Promise<LifecycleNode> => {
    const response = await apiClient.get(`/api/admin/lifecycle/nodes/${nodeId}`, {
      params: { environment },
    });
    return response.data;
  },

  // Get services for a node
  getNodeServices: async (nodeId: string, environment: string = 'prod'): Promise<LifecycleService[]> => {
    const response = await apiClient.get(`/api/admin/lifecycle/nodes/${nodeId}/services`, {
      params: { environment },
    });
    return response.data;
  },

  // Start node(s)
  startNodes: async (nodeIds: string[] | null, environment: string = 'prod') => {
    const response = await apiClient.post('/api/admin/lifecycle/nodes/start', {
      node_ids: nodeIds,
      parallel: true,
    }, {
      params: { environment },
      timeout: 300000, // 5 min timeout
    });
    return response.data;
  },

  // Stop node(s)
  stopNodes: async (nodeIds: string[] | null, environment: string = 'prod') => {
    const response = await apiClient.post('/api/admin/lifecycle/nodes/stop', {
      node_ids: nodeIds,
    }, {
      params: { environment },
      timeout: 300000, // 5 min timeout
    });
    return response.data;
  },

  // Get startup progress for a node
  getProgress: async (nodeId: string, environment: string = 'prod'): Promise<LifecycleProgress> => {
    const response = await apiClient.get(`/api/admin/lifecycle/nodes/${nodeId}/progress`, {
      params: { environment },
    });
    return response.data;
  },

  // Get all startup progress
  getAllProgress: async (environment: string = 'prod') => {
    const response = await apiClient.get('/api/admin/lifecycle/progress', {
      params: { environment },
    });
    return response.data;
  },

  // Get health checks history
  getHealthChecks: async (nodeId?: string, limit: number = 100, environment: string = 'prod'): Promise<HealthCheckResult[]> => {
    const response = await apiClient.get('/api/admin/lifecycle/health-checks', {
      params: { node_id: nodeId, limit, environment },
    });
    return response.data;
  },

  // Get audit logs
  getAuditLogs: async (targetId?: string, limit: number = 100, environment: string = 'prod'): Promise<AuditLogEntry[]> => {
    const response = await apiClient.get('/api/admin/lifecycle/audit', {
      params: { target_id: targetId, limit, environment },
    });
    return response.data;
  },

  // Get checklists
  getChecklists: async (category?: string, environment: string = 'prod'): Promise<Checklist[]> => {
    const response = await apiClient.get('/api/admin/lifecycle/checklists', {
      params: { category, environment },
    });
    return response.data;
  },

  // Get startup sequences
  getStartupSequences: async (environment: string = 'prod'): Promise<StartupSequenceStep[]> => {
    const response = await apiClient.get('/api/admin/lifecycle/startup-sequences', {
      params: { environment },
    });
    return response.data;
  },

  // Health monitor status
  getMonitorStatus: async (environment: string = 'prod') => {
    const response = await apiClient.get('/api/admin/lifecycle/monitor/status', {
      params: { environment },
    });
    return response.data;
  },

  // Get health summary
  getHealthSummary: async (environment: string = 'prod'): Promise<HealthSummary> => {
    const response = await apiClient.get('/api/admin/lifecycle/monitor/summary', {
      params: { environment },
    });
    return response.data;
  },

  // Start health monitor
  startMonitor: async (environment: string = 'prod') => {
    const response = await apiClient.post('/api/admin/lifecycle/monitor/start', {}, {
      params: { environment },
    });
    return response.data;
  },

  // Stop health monitor
  stopMonitor: async (environment: string = 'prod') => {
    const response = await apiClient.post('/api/admin/lifecycle/monitor/stop', {}, {
      params: { environment },
    });
    return response.data;
  },

  // Run health check now
  runHealthCheck: async (environment: string = 'prod') => {
    const response = await apiClient.post('/api/admin/lifecycle/monitor/check-now', {}, {
      params: { environment },
      timeout: 300000, // 5 min for full health check
    });
    return response.data;
  },

  // Sync nodes from registry file to database
  syncFromRegistry: async (environment: string = 'prod') => {
    const response = await apiClient.post('/api/admin/lifecycle/sync-registry', {}, {
      params: { environment },
    });
    return response.data;
  },

  // ==========================================================================
  // SSE-Based Operation Endpoints (Enterprise Progress Visualization)
  // ==========================================================================

  // Start nodes with SSE operation tracking
  startOperation: async (nodeIds: string[] | null = null): Promise<{
    operation_id: string;
    operation_type: string;
    status: string;
    message: string;
    node_count: number;
  }> => {
    const response = await apiClient.post('/api/admin/lifecycle/operations/start', {
      node_ids: nodeIds,
      parallel: true,
    }, {
      timeout: 30000, // Quick response, operation runs in background
    });
    return response.data;
  },

  // Stop nodes with SSE operation tracking
  stopOperation: async (nodeIds: string[] | null = null): Promise<{
    operation_id: string;
    operation_type: string;
    status: string;
    message: string;
    node_count: number;
  }> => {
    const response = await apiClient.post('/api/admin/lifecycle/operations/stop', {
      node_ids: nodeIds,
    }, {
      timeout: 30000,
    });
    return response.data;
  },

  // Get operation status (polling fallback)
  getOperationStatus: async (operationId: string) => {
    const response = await apiClient.get(`/api/admin/lifecycle/operations/${operationId}`);
    return response.data;
  },

  // Retry a failed node in an operation
  retryNode: async (operationId: string, nodeId: string) => {
    const response = await apiClient.post(
      `/api/admin/lifecycle/operations/${operationId}/retry/${nodeId}`
    );
    return response.data;
  },

  // Verify mesh health with SSE operation tracking
  verifyOperation: async (nodeIds: string[] | null = null, crossCheckExisting = true): Promise<{
    operation_id: string;
    operation_type: string;
    status: string;
    message: string;
    node_count: number;
  }> => {
    const response = await apiClient.post('/api/admin/lifecycle/operations/verify', {
      node_ids: nodeIds,
      cross_check_existing: crossCheckExisting,
    }, {
      timeout: 30000,
    });
    return response.data;
  },

  // Get SSE stream URL for an operation
  getOperationStreamUrl: (operationId: string): string => {
    return `/api/admin/lifecycle/operations/${operationId}/stream`;
  },
};

// --- Investor Demos API (5 impressive visual demos) ---
export interface InvestorDemoStep {
  step_number: number;
  name: string;
  status: string;
  gateway?: string;
  location?: string;
  flag?: string;
  exit_ip?: string;
  latency_ms?: number;
  switch_time_ms?: number;
  started_at?: string;
  completed_at?: string;
  message?: string;
  details?: Record<string, unknown>;
}

export interface InvestorDemoInfo {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  duration: string;
  tags: string[];
  highlights: string[];
}

export interface InvestorDemoResponse {
  demo_id: string;
  demo_type: string;
  title: string;
  description: string;
  status: string;
  current_step: number;
  total_steps: number;
  steps: InvestorDemoStep[];
  started_at?: string;
  completed_at?: string;
  total_duration_ms?: number;
  summary?: Record<string, unknown>;
  error?: string;
}

export interface AlgorithmCompareResult {
  algorithm: string;
  path: string[];
  total_cost_ms: number;
  computation_time_ms: number;
  quality_grade: string;
  details: Record<string, unknown>;
}

export interface PathComparisonResponse {
  source: string;
  destination: string;
  bgp_info: Record<string, unknown>;
  ddarp_info: Record<string, unknown>;
  algorithms: AlgorithmCompareResult[];
  topology: Record<string, unknown>;
  winner: string;
  improvement_ms: number;
}

export const investorDemosApi = {
  // Get list of available investor demos
  getDemos: async (): Promise<{ demos: InvestorDemoInfo[]; client: Record<string, string> }> => {
    const response = await apiClient.get('/api/admin/investor-demos/demos');
    return response.data;
  },

  // Start a demo
  startDemo: async (demoType: string): Promise<InvestorDemoResponse> => {
    const response = await apiClient.post(`/api/admin/investor-demos/start/${demoType}`);
    return response.data;
  },

  // Get demo status
  getStatus: async (demoId: string): Promise<InvestorDemoResponse> => {
    const response = await apiClient.get(`/api/admin/investor-demos/status/${demoId}`);
    return response.data;
  },

  // Stop a demo
  stopDemo: async (demoId: string): Promise<{ message: string; demo_id: string }> => {
    const response = await apiClient.post(`/api/admin/investor-demos/stop/${demoId}`);
    return response.data;
  },

  // Get available gateways
  getGateways: async (): Promise<{ gateways: Record<string, unknown> }> => {
    const response = await apiClient.get('/api/admin/investor-demos/gateways');
    return response.data;
  },

  // Compare path algorithms
  compareAlgorithms: async (source: string, destination: string): Promise<PathComparisonResponse> => {
    const response = await apiClient.get('/api/admin/investor-demos/algorithms/compare', {
      params: { source, destination },
    });
    return response.data;
  },
};

// --- Nodes Health Dashboard ---
import type {
  HealthDashboardResponse,
  InfrastructureHealthResponse,
} from '@/types/nodes';

export const nodesDashboardApi = {
  getHealthDashboard: async (): Promise<HealthDashboardResponse> => {
    const response = await apiClient.get('/api/admin/nodes/health-dashboard');
    return response.data;
  },

  getInfrastructureHealth: async (): Promise<InfrastructureHealthResponse> => {
    const response = await apiClient.get('/api/admin/infrastructure/health');
    return response.data;
  },
};

// --- Monitoring & Pulse Groups (Rebuilt Tabs) ---
import type {
  MonitoringSummary,
  GroupedMatrix,
  ServiceHealthResponse,
  RaftDetail,
  StitchingStatus,
} from '@/types/monitoring';

export const monitoringApi = {
  /** All KPIs, protocol phases, and node metadata in one call */
  getSummary: async (): Promise<MonitoringSummary> => {
    const response = await apiClient.get('/api/admin/monitoring/summary');
    return response.data;
  },

  /** Group-to-group + cloud-to-cloud + full node matrix */
  getGroupedMatrix: async (): Promise<GroupedMatrix> => {
    const response = await apiClient.get('/api/admin/monitoring/matrix-grouped');
    return response.data;
  },

  /** Per-node service status table */
  getServiceHealth: async (): Promise<ServiceHealthResponse> => {
    const response = await apiClient.get('/api/admin/monitoring/service-health');
    return response.data;
  },

  /** Raft state from all border nodes */
  getRaftDetail: async (): Promise<RaftDetail> => {
    const response = await apiClient.get('/api/admin/monitoring/raft-detail');
    return response.data;
  },

  /** Stitching pipeline + BGP injection status */
  getStitching: async (): Promise<StitchingStatus> => {
    const response = await apiClient.get('/api/admin/monitoring/stitching');
    return response.data;
  },
};


// ============================================================================
// Data Room API (P5, 2026-05-23)
// ============================================================================
// Mirrors the 4 backend routers (folders / items / browse / internal).
// Endpoints return 404 when the backend's DATA_ROOM_ENABLED=false; callers
// should treat that as "feature disabled" rather than a hard error.
// ============================================================================

export type DataRoomAccess = 'everyone' | 'admin' | 'admin_partner';
export type DataRoomItemKind = 'file' | 'slide';
export type DataRoomScanStatus = 'pending' | 'clean' | 'infected' | 'skipped';
export type DataRoomPreviewStatus = 'not_applicable' | 'pending' | 'ready' | 'failed';

export interface DataRoomFolder {
  id: number;
  parent_id: number | null;
  name: string;
  description: string | null;
  /** Access level: everyone (all read, admin manage) | admin (admin only) |
   *  admin_partner (admin + partner read + full CRUD). 2026-06-06. */
  access: DataRoomAccess;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface DataRoomItem {
  id: number;
  folder_id: number | null;
  kind: DataRoomItemKind;
  title: string;
  description: string | null;
  filename: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  thumbnail_s3_key: string | null;
  preview_status: DataRoomPreviewStatus;
  scan_status: DataRoomScanStatus;
  embed_url: string | null;
  internal_path: string | null;
  tags: string[];
  current_version: number;
  view_count: number;
  download_count: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  /** Access level of the parent folder; 'admin' for root items. (Phase 2) */
  folder_access?: DataRoomAccess;
}

export interface DataRoomItemBrief {
  id: number;
  kind: DataRoomItemKind;
  title: string;
  mime_type: string | null;
  size_bytes: number | null;
  thumbnail_s3_key: string | null;
  tags: string[];
  current_version: number;
  view_count: number;
  download_count: number;
  created_at: string | null;
  updated_at: string | null;
  scan_status: DataRoomScanStatus;
  preview_status: DataRoomPreviewStatus;
  /** Access level of the parent folder; 'admin' for root items. (Phase 2) */
  folder_access?: DataRoomAccess;
}

export interface DataRoomFolderChildren {
  folders: DataRoomFolder[];
  items: DataRoomItemBrief[];
}

export interface DataRoomItemPage {
  total: number;
  page: number;
  per_page: number;
  items: DataRoomItem[];
}

export interface DataRoomVersion {
  id: number;
  version: number;
  s3_key: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number;
  uploaded_at: string;
  notes: string | null;
}

export interface DataRoomUploadHandle {
  upload_id: string;
  post: { url: string; fields: Record<string, string> };
  expires_at: string;
  filename: string;
}

export interface DataRoomPresignedUrl {
  url: string;
  expires_in: number;
}

export interface DataRoomStats {
  total_folders: number;
  total_items: number;
  total_files: number;
  total_slides: number;
  deleted_folders: number;
  deleted_items: number;
  total_size_bytes: number;
  total_views: number;
  total_downloads: number;
}

export interface DataRoomTrash {
  folders: Array<{
    id: number;
    parent_id: number | null;
    name: string;
    access: DataRoomAccess;
    deleted_at: string | null;
  }>;
  items: DataRoomItem[];
}

export interface DataRoomActivityEntry {
  id: number;
  timestamp: string;
  operation: string;
  target_type: string;
  target_id: string | null;
  initiated_by: string | null;
  status: string;
  details: Record<string, unknown> | null;
}

export interface DataRoomFavoriteResponse {
  favorited: boolean;
}

/** Phase 9 — Data Room v2. Returned by POST /items/:id/share-link. */
export interface DataRoomShareLinkResponse {
  token: string;
  url: string;
  expires_at: string | null;
  allow_download: boolean;
  created_at: string;
}

/** Phase 9 — Data Room v2. Returned by GET /items/:id/share-links.
 *  Lists active (non-revoked) tokens for an item, by 8-char preview. */
export interface DataRoomShareLinkListItem {
  token_preview: string;
  expires_at: string | null;
  created_at: string;
  allow_download: boolean;
  view_count: number;
  last_viewed_at: string | null;
}

/** Phase 9 — Data Room v2. Returned by GET /api/public/share/:token.
 *  Subset of item metadata exposed to unauthenticated viewers. */
export interface SharedItemResponse {
  item_id: number;
  title: string;
  kind: DataRoomItemKind;
  description: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  filename: string | null;
  embed_url: string | null;
  internal_path: string | null;
  view_url: string | null;
  allow_download: boolean;
  expires_at: string | null;
}

export type DataRoomShareExpiry = '24h' | '7d' | '30d' | 'forever';

export interface DataRoomListQuery {
  folder_id?: number | null;
  q?: string;
  tags?: string[];
  kind?: DataRoomItemKind;
  sort?: string;
  page?: number;
  per_page?: number;
}

const DR_BASE = '/api/admin/data-room';

export const dataRoomApi = {
  // ---- Folders ----
  listFolderChildren: async (parent_id?: number | null): Promise<DataRoomFolderChildren> => {
    const params: Record<string, unknown> = {};
    if (parent_id !== undefined && parent_id !== null) params.parent_id = parent_id;
    const r = await apiClient.get(`${DR_BASE}/folders`, { params });
    return r.data;
  },

  getFolder: async (id: number): Promise<DataRoomFolder> => {
    const r = await apiClient.get(`${DR_BASE}/folders/${id}`);
    return r.data;
  },

  getFolderBreadcrumbs: async (id: number): Promise<DataRoomFolder[]> => {
    const r = await apiClient.get(`${DR_BASE}/folders/${id}/breadcrumbs`);
    return r.data;
  },

  createFolder: async (payload: {
    parent_id?: number | null;
    name: string;
    description?: string | null;
    /** None/omitted = inherit parent (or 'everyone' at root). Partners can't set this. */
    access?: DataRoomAccess;
  }): Promise<DataRoomFolder> => {
    const r = await apiClient.post(`${DR_BASE}/folders`, payload);
    return r.data;
  },

  updateFolder: async (
    id: number,
    payload: {
      name?: string;
      description?: string | null;
      access?: DataRoomAccess;
      new_parent_id?: number | null;
      move_to_root?: boolean;
    }
  ): Promise<DataRoomFolder> => {
    const r = await apiClient.patch(`${DR_BASE}/folders/${id}`, payload);
    return r.data;
  },

  copyFolder: async (
    id: number,
    destParentId?: number | null
  ): Promise<DataRoomFolder> => {
    const r = await apiClient.post(`${DR_BASE}/folders/${id}/copy`, {
      dest_parent_id: destParentId ?? null,
    });
    return r.data;
  },

  deleteFolder: async (id: number): Promise<void> => {
    await apiClient.delete(`${DR_BASE}/folders/${id}`);
  },

  restoreFolder: async (id: number): Promise<DataRoomFolder> => {
    const r = await apiClient.post(`${DR_BASE}/folders/${id}/restore`);
    return r.data;
  },

  purgeFolder: async (id: number): Promise<void> => {
    await apiClient.delete(`${DR_BASE}/folders/${id}/purge`);
  },

  // ---- Items ----
  listItems: async (query: DataRoomListQuery = {}): Promise<DataRoomItemPage> => {
    const r = await apiClient.get(`${DR_BASE}/items`, { params: query });
    return r.data;
  },

  getItem: async (id: number): Promise<DataRoomItem> => {
    const r = await apiClient.get(`${DR_BASE}/items/${id}`);
    return r.data;
  },

  listVersions: async (id: number): Promise<DataRoomVersion[]> => {
    const r = await apiClient.get(`${DR_BASE}/items/${id}/versions`);
    return r.data;
  },

  getViewUrl: async (id: number): Promise<DataRoomPresignedUrl> => {
    const r = await apiClient.get(`${DR_BASE}/items/${id}/view-url`);
    return r.data;
  },

  getDownloadUrl: async (id: number): Promise<DataRoomPresignedUrl> => {
    const r = await apiClient.get(`${DR_BASE}/items/${id}/download-url`);
    return r.data;
  },

  getThumbnailUrl: async (id: number): Promise<DataRoomPresignedUrl> => {
    const r = await apiClient.get(`${DR_BASE}/items/${id}/thumbnail-url`);
    return r.data;
  },

  /** Office-doc PDF preview (P9 Lambda fills it in). 404 until ready. */
  getPreviewUrl: async (id: number): Promise<DataRoomPresignedUrl> => {
    const r = await apiClient.get(`${DR_BASE}/items/${id}/preview-url`);
    return r.data;
  },

  uploadInit: async (payload: {
    folder_id: number;
    filename: string;
    size_bytes: number;
    mime: string;
  }): Promise<DataRoomUploadHandle> => {
    const r = await apiClient.post(`${DR_BASE}/items/upload-init`, payload);
    return r.data;
  },

  uploadCommit: async (payload: {
    upload_id: string;
    title: string;
    description?: string | null;
    tags?: string[];
  }): Promise<DataRoomItem> => {
    const r = await apiClient.post(`${DR_BASE}/items/upload-commit`, payload);
    return r.data;
  },

  createSlide: async (payload: {
    folder_id: number;
    title: string;
    // Provide exactly one of embed_url OR internal_path. Server-side
    // SlideCreateIn enforces the XOR.
    embed_url?: string | null;
    internal_path?: string | null;
    description?: string | null;
    tags?: string[];
  }): Promise<DataRoomItem> => {
    const r = await apiClient.post(`${DR_BASE}/items/slides`, payload);
    return r.data;
  },

  patchItem: async (
    id: number,
    payload: {
      title?: string;
      description?: string | null;
      tags?: string[];
      folder_id?: number;
    }
  ): Promise<DataRoomItem> => {
    const r = await apiClient.patch(`${DR_BASE}/items/${id}`, payload);
    return r.data;
  },

  copyItem: async (
    id: number,
    destFolderId?: number | null
  ): Promise<DataRoomItem> => {
    const r = await apiClient.post(`${DR_BASE}/items/${id}/copy`, {
      dest_folder_id: destFolderId ?? null,
    });
    return r.data;
  },

  addVersion: async (
    id: number,
    payload: { upload_id: string; notes?: string | null }
  ): Promise<DataRoomItem> => {
    const r = await apiClient.post(`${DR_BASE}/items/${id}/versions`, payload);
    return r.data;
  },

  makeVersionCurrent: async (id: number, version: number): Promise<DataRoomItem> => {
    const r = await apiClient.post(
      `${DR_BASE}/items/${id}/versions/${version}/make-current`,
    );
    return r.data;
  },

  deleteItem: async (id: number): Promise<void> => {
    await apiClient.delete(`${DR_BASE}/items/${id}`);
  },

  restoreItem: async (id: number): Promise<DataRoomItem> => {
    const r = await apiClient.post(`${DR_BASE}/items/${id}/restore`);
    return r.data;
  },

  purgeItem: async (id: number): Promise<void> => {
    await apiClient.delete(`${DR_BASE}/items/${id}/purge`);
  },

  toggleFavorite: async (id: number): Promise<DataRoomFavoriteResponse> => {
    const r = await apiClient.post(`${DR_BASE}/items/${id}/favorite`);
    return r.data;
  },

  /** Phase 9 — Data Room v2. Create an expiring share-link for an item. */
  createShareLink: async (
    id: number,
    payload: {
      expires_in: DataRoomShareExpiry;
      allow_download: boolean;
    },
  ): Promise<DataRoomShareLinkResponse> => {
    const r = await apiClient.post(`${DR_BASE}/items/${id}/share-link`, payload);
    return r.data;
  },

  /** Phase 9 — Data Room v2. List active share-links for an item. */
  listShareLinks: async (id: number): Promise<DataRoomShareLinkListItem[]> => {
    const r = await apiClient.get(`${DR_BASE}/items/${id}/share-links`);
    return r.data;
  },

  /** Phase 9 — Data Room v2. Revoke a share-link by token preview. */
  revokeShareLink: async (id: number, tokenPreview: string): Promise<void> => {
    await apiClient.delete(
      `${DR_BASE}/items/${id}/share-links/${encodeURIComponent(tokenPreview)}`,
    );
  },

  /** Phase 9 — Public, no auth. Fetch shared-item metadata + view URL. */
  getSharedItem: async (token: string): Promise<SharedItemResponse> => {
    const r = await apiClient.get(`/api/public/share/${encodeURIComponent(token)}`);
    return r.data;
  },

  /** Phase 9 — Public, no auth. Fetch a presigned download URL for a
   *  file-kind share with allow_download=true. */
  getSharedDownload: async (
    token: string,
  ): Promise<{ url: string; expires_in: number }> => {
    const r = await apiClient.get(
      `/api/public/share/${encodeURIComponent(token)}/download`,
    );
    return r.data;
  },

  // ---- Browse ----
  listRecent: async (limit = 20): Promise<DataRoomItem[]> => {
    const r = await apiClient.get(`${DR_BASE}/recent`, { params: { limit } });
    return r.data;
  },

  listFavorites: async (): Promise<DataRoomItem[]> => {
    const r = await apiClient.get(`${DR_BASE}/favorites`);
    return r.data;
  },

  listTrash: async (): Promise<DataRoomTrash> => {
    const r = await apiClient.get(`${DR_BASE}/trash`);
    return r.data;
  },

  getStats: async (): Promise<DataRoomStats> => {
    const r = await apiClient.get(`${DR_BASE}/stats`);
    return r.data;
  },

  getActivity: async (limit = 50): Promise<DataRoomActivityEntry[]> => {
    const r = await apiClient.get(`${DR_BASE}/activity`, { params: { limit } });
    return r.data;
  },

  search: async (query: DataRoomListQuery = {}): Promise<DataRoomItemPage> => {
    const r = await apiClient.get(`${DR_BASE}/search`, { params: query });
    return r.data;
  },

  listTags: async (limit = 100): Promise<Array<{ tag: string; count: number }>> => {
    const r = await apiClient.get(`${DR_BASE}/tags`, { params: { limit } });
    return r.data;
  },
};


// ---------------------------------------------------------------------------
// Public API (Public Pages Refinements Phase 3 — 2026-05-26)
// ---------------------------------------------------------------------------

export interface ContactSubmissionRequest {
  name: string;
  email: string;
  company?: string | null;
  message: string;
  /** Honeypot — bots fill, humans never see. If non-empty, server
   *  silently returns 202 with submission_id=null. */
  h_email_address?: string;
}

export interface ContactSubmissionResponse {
  received: boolean;
  submission_id: number | null;
}

export const publicApi = {
  submitContact: async (
    payload: ContactSubmissionRequest,
  ): Promise<ContactSubmissionResponse> => {
    const r = await apiClient.post('/api/public/contact', payload);
    return r.data;
  },
};


export default apiClient;
